import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ServiceIcon, priceLabel } from "@/components/public/common";
import { PageHero } from "./Services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: service, isLoading, isError } = useQuery({ queryKey: ["service", slug], queryFn: () => api.get(`/services/${slug}`).then(r => r.data) });
  const { data: pricing } = useQuery({ queryKey: ["pricing", service?.category], queryFn: () => api.get(`/pricing`, { params: { category: service?.category } }).then(r => r.data.pricing), enabled: !!service });

  if (isLoading) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-slate-500">Loading…</div>;
  if (isError || !service) return <div className="max-w-7xl mx-auto px-6 py-24 text-center"><p className="text-slate-600">Service not found.</p><Link to="/services" className="text-electric">Back to services</Link></div>;

  return (
    <div>
      <PageHero label={service.category} title={service.name} subtitle={service.shortDescription} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-electric to-tech text-white grid place-items-center mb-6"><ServiceIcon name={service.icon} className="h-8 w-8" /></div>
          <h2 className="font-heading text-2xl font-bold text-navy">What's included</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {(service.features || []).map((f, i) => (
              <div key={i} className="flex gap-2.5 rounded-lg border border-slate-200 p-4"><CheckCircle2 className="h-5 w-5 text-electric shrink-0" /><span className="text-sm text-slate-700">{f}</span></div>
            ))}
          </div>
          <div className="mt-8 rounded-xl bg-amber-50 border border-amber-200 p-5 flex gap-3">
            <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">We never diagnose hardware conclusively without inspection, and we never guarantee repair outcomes in advance. Initial AI guidance from Nexus is not a confirmed technical diagnosis.</p>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="rounded-xl border-2 border-electric/20 p-6 sticky top-20">
            <p className="font-mono text-xs uppercase tracking-wider text-electric">Starting price</p>
            <p className="mt-1 text-3xl font-heading font-bold text-navy">{service.startingPrice != null ? `$${service.startingPrice}` : "Quote based"}</p>
            <p className="text-xs text-slate-500 mt-1">Final estimates may change after review.</p>
            <Link to="/request-service"><Button className="w-full mt-5 bg-electric hover:bg-tech" data-testid="service-request-cta">{service.ctaText || "Request Service"}</Button></Link>
            <Link to="/chat"><Button variant="outline" className="w-full mt-2">Ask Nexus</Button></Link>
          </div>
        </aside>
      </section>

      {pricing?.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <h2 className="font-heading text-2xl font-bold text-navy mb-6">{service.category} pricing</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pricing.map((p) => (
                <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-6">
                  <h3 className="font-heading font-semibold text-navy">{p.packageName}</h3>
                  <p className="text-2xl font-heading font-bold text-navy mt-1">{priceLabel(p)}</p>
                  <p className="text-sm text-slate-600 mt-1">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
